import { Copy, Minus, TrendingDown, TrendingUp } from "lucide-react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { cn } from "@/shared/lib/utils"
import { copyToClipboard } from "@/shared/lib/utils/copy-to-clipboard"
import { WebApp } from "@/shared/telegram"

import type { LeaderboardEntry } from "../../model/types"

const { notificationOccurred } = WebApp.HapticFeedback

interface LeaderboardTableRowProps {
  entry: LeaderboardEntry
  isCurrentUser?: boolean
}

/** Renders the `<td>` cells of a leaderboard row — used inside TableVirtuoso rows. */
export const LeaderboardTableRow: React.FC<LeaderboardTableRowProps> = ({ entry, isCurrentUser = false }) => {
  const { t } = useTranslation()
  const rankTone = entry.position <= 3 ? "text-yellow-500" : "text-gray-500"
  const cell = cn(
    "border-b border-gray-800/80 px-2 py-2 align-middle text-sm",
    entry.position === 1 && "bg-yellow-500/20",
    isCurrentUser && "bg-yellow-500/15"
  )

  const handleCopyUserId: React.MouseEventHandler<HTMLButtonElement> = () => {
    void copyToClipboard({
      message: String(entry.userId),
      onSuccess: () => {
        notificationOccurred("success")
        toast.success(t("clipboard-copy-success", "Copied to clipboard"))
      },
      onError: () => {
        notificationOccurred("error")
        toast.error(t("clipboard-copy-error", "Failed to copy"))
      }
    })
  }

  return (
    <>
      <td className={cn(cell, "whitespace-nowrap text-center")}>
        <span className={cn("font-bold", rankTone)}>#{entry.position}</span>
      </td>
      <td className={cn(cell, "w-full")}>
        <div className="grid grid-cols-[auto_minmax(0,_1fr)] items-center gap-x-1.5 gap-y-0.5">
          {entry.avatarUrl ? (
            <img
              src={entry.avatarUrl}
              alt={t("user-avatar-alt", "{{name}}'s avatar", { name: entry.displayName })}
              width={28}
              height={28}
              className="row-span-2 size-7 rounded-full object-cover"
            />
          ) : (
            <div className="row-span-2 flex size-7 items-center justify-center rounded-full bg-emerald-700 text-xs font-semibold text-gray-100">
              {entry.displayName.slice(0, 1).toUpperCase()}
            </div>
          )}
          <dl className="row-span-2 grid grid-rows-subgrid">
            <dt className="truncate font-semibold text-gray-100">{entry.displayName}</dt>
            <dd className="ms-0 flex items-center gap-1.5 text-xs text-gray-400">
              <span className="select-all tabular-nums">{entry.userId}</span>
              <button
                type="button"
                onClick={handleCopyUserId}
                className="relative rounded p-0.5 text-yellow-500 transition-colors before:absolute before:-inset-3.5 before:content-[''] focus-visible:bg-yellow-500/10 focus-visible:text-yellow-400 focus-visible:outline-none hover-device:hover:bg-yellow-500/10 hover-device:hover:text-yellow-400">
                <Copy
                  aria-hidden="true"
                  focusable="false"
                  className="size-3"
                />
                <span className="sr-only">{t("copy-to-clipboard", "Copy to clipboard")}</span>
              </button>
            </dd>
          </dl>
        </div>
      </td>
      <td className={cn(cell, "whitespace-nowrap text-center font-semibold text-gray-300")}>{entry.level}</td>
      <td className={cn(cell, "whitespace-nowrap text-center font-semibold tabular-nums text-gray-100")}>{entry.xp}</td>
      <td className={cn(cell, "whitespace-nowrap text-center")}>
        <ChangeBadge value={entry.xpChange} />
      </td>
    </>
  )
}

interface ChangeBadgeProps {
  value: number
}

const ChangeBadge: React.FC<ChangeBadgeProps> = ({ value }) => {
  if (value > 0) {
    return (
      <span className="inline-flex items-center justify-end gap-1 text-xs font-semibold tabular-nums text-emerald-400">
        <TrendingUp
          aria-hidden="true"
          focusable="false"
          className="size-3"
        />
        {value}
      </span>
    )
  }

  if (value < 0) {
    return (
      <span className="inline-flex items-center justify-end gap-1 text-xs font-semibold tabular-nums text-red-400">
        <TrendingDown
          aria-hidden="true"
          focusable="false"
          className="size-3"
        />
        {Math.abs(value)}
      </span>
    )
  }

  return (
    <span className="inline-flex items-center justify-end text-gray-600">
      <Minus
        aria-hidden="true"
        focusable="false"
        className="size-3"
      />
    </span>
  )
}
